import React, { useEffect, useRef, useState } from 'react'
import { SUMMARY_PALETTE } from './summary.palette'

// MF-526 — Reusable KPI mini-chart (line or bar) with a visible Y-axis scale + gridlines + X term
// labels, matching the approved BI-style design. Pure SVG, no chart lib — light and exact. A term
// with `v === null` is "not reached yet": it keeps its slot on the axis but draws nothing (never a
// misleading zero). This is one of the reusable building blocks for the future chart bank.

// "Nice" y-axis: 0 → a rounded top with ~4 clean ticks (57→60, 280→300, 98→100).
const niceTicks = (dataMax, targetMax) => {
  const max = targetMax || dataMax || 1
  const rawStep = max / 4
  const pow = Math.pow(10, Math.floor(Math.log10(rawStep || 1)))
  const norm = rawStep / pow
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * pow
  const top = Math.max(step, Math.ceil(max / step) * step)
  const ticks = []
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v)
  return { top, ticks }
}
const fmtTick = (v) => (Number.isInteger(v) ? String(v) : String(+v.toFixed(1)))

/**
 * @param {Object} props
 * @param {'line'|'bar'} props.type
 * @param {Array<{t:string, v:number|null}>} props.trend
 * @param {number} [props.yMax]  - fixed axis top (e.g. 100 for %, 4 for GPA); otherwise auto.
 * @param {string} [props.color] - accent hex for the marks.
 * @param {number} [props.height] - chart height in px (default 200).
 */
const MiniChart = ({
  type = 'line',
  trend = [],
  yMax,
  color = SUMMARY_PALETTE.good,
  unit = '',
  height = 200
}) => {
  // MF-542 — hovered point index → tooltip with the term + value.
  const [hover, setHover] = useState(null)
  // MF-558 — draw in real pixels (measured container width) instead of scaling a fixed viewBox:
  // a scaled viewBox blew the axis text up on wide cards and let the edge labels spill out.
  const wrapRef = useRef(null)
  const [W, setW] = useState(0)
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return undefined
    const measure = () => setW(Math.floor(el.getBoundingClientRect().width))
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [trend.length])
  if (!trend.length) return null
  // MF-554 — height is a prop (default 200 = the Student Summary card) so a compact Board tile fits.
  const H = height
  const FS = 11 // axis font size (px)
  const GY = 36 // room for the y tick labels
  const topPad = 12
  const bottom = H - 26
  const right = Math.max(W - 4, GY + 40)
  const n = trend.length

  const dataMax = Math.max(...trend.map((d) => d.v).filter((v) => v != null), 1)
  const { top: yTop, ticks } = niceTicks(dataMax, yMax)
  const cx = (i) => GY + ((i + 0.5) * (right - GY)) / n
  const x = cx
  const y = (v) => bottom - (v / yTop) * (bottom - topPad)

  const nodes = []

  ticks.forEach((tv, k) => {
    nodes.push(
      <line
        key={`g${k}`}
        x1={GY}
        y1={y(tv)}
        x2={right}
        y2={y(tv)}
        stroke={SUMMARY_PALETTE.grid}
        strokeWidth='1'
      />
    )
    nodes.push(
      <text
        key={`yt${k}`}
        x={GY - 8}
        y={y(tv) + 4}
        textAnchor='end'
        fill={SUMMARY_PALETTE.faint}
        fontSize={FS}
      >
        {fmtTick(tv)}
      </text>
    )
  })

  if (type === 'bar') {
    const bw = Math.min(((right - GY) / n) * 0.55, 56)
    const lastIdx = trend.reduce((a, d, i) => (d.v != null ? i : a), 0)
    trend.forEach((d, i) => {
      if (d.v == null) return
      nodes.push(
        <rect
          key={`b${i}`}
          x={cx(i) - bw / 2}
          y={y(d.v)}
          width={bw}
          height={bottom - y(d.v)}
          rx='2'
          fill={color}
          fillOpacity={i === lastIdx ? 1 : 0.4}
        />
      )
    })
  } else {
    const pts = trend.map((d, i) => ({ i, v: d.v })).filter((p) => p.v != null)
    if (pts.length) {
      const areaD =
        `M ${x(pts[0].i)},${bottom} L ` +
        pts.map((p) => `${x(p.i)},${y(p.v)}`).join(' L ') +
        ` L ${x(pts[pts.length - 1].i)},${bottom} Z`
      nodes.push(<path key='area' d={areaD} fill={color} fillOpacity='0.08' stroke='none' />)
      nodes.push(
        <polyline
          key='line'
          points={pts.map((p) => `${x(p.i)},${y(p.v)}`).join(' ')}
          fill='none'
          stroke={color}
          strokeWidth='2'
          strokeLinejoin='round'
          strokeLinecap='round'
        />
      )
      pts.forEach((p, k) => {
        const last = k === pts.length - 1
        nodes.push(
          <circle
            key={`d${k}`}
            cx={x(p.i)}
            cy={y(p.v)}
            r={last ? 4 : 3}
            fill={last ? color : SUMMARY_PALETTE.surface}
            stroke={color}
            strokeWidth='2'
          />
        )
      })
    }
  }

  trend.forEach((d, i) => {
    nodes.push(
      <text
        key={`xt${i}`}
        x={type === 'bar' ? cx(i) : x(i)}
        y={H - 6}
        textAnchor='middle'
        fill={SUMMARY_PALETTE.faint}
        fontSize={FS}
        opacity={d.v == null ? 0.45 : 1}
      >
        {d.t}
      </text>
    )
  })

  // MF-542 — per-column transparent hover targets (full height) → drive the tooltip.
  const colW = (right - GY) / n
  trend.forEach((d, i) => {
    const mx = type === 'bar' ? cx(i) : x(i)
    nodes.push(
      <rect
        key={`hit${i}`}
        x={mx - colW / 2}
        y={topPad}
        width={colW}
        height={bottom - topPad}
        fill='transparent'
        onMouseEnter={() => setHover(i)}
        onMouseLeave={() => setHover(null)}
      />
    )
  })

  // MF-542 — tooltip for the hovered column: "Term X" + its value (only when the term has data).
  let tooltip = null
  if (hover != null && trend[hover] && trend[hover].v != null) {
    const d = trend[hover]
    const mx = type === 'bar' ? cx(hover) : x(hover)
    const valText = `${fmtTick(d.v)}${unit || ''}`
    // Roomy box: estimate each line at its own size (term 11px muted, value 14px bold) and add
    // generous side padding so the text never crowds the edges.
    const tw = Math.max(String(d.t).length * 6.6, valText.length * 8.6) + 22
    const th = 42
    const gap = 8 // room for the little pointer between the mark and the box
    const bx = Math.max(2, Math.min(mx - tw / 2, W - tw - 2))
    // Prefer the box above the mark; if it doesn't fit (point near the top), flip it below so the
    // pointer always touches the mark instead of floating in the empty space above.
    const above = y(d.v) - th - gap >= 2
    const by = above ? y(d.v) - th - gap : y(d.v) + gap
    const cxp = bx + tw / 2
    // Little pointer centered under the mark, clamped to stay within the box; points down when the
    // box sits above the mark, up when it sits below.
    const px = Math.max(bx + 8, Math.min(mx, bx + tw - 8))
    const pointer = above
      ? `${px - 4},${by + th} ${px + 4},${by + th} ${px},${by + th + 5}`
      : `${px - 4},${by} ${px + 4},${by} ${px},${by - 5}`
    tooltip = (
      <g key='tt' pointerEvents='none'>
        <rect x={bx} y={by} width={tw} height={th} rx='5' fill={SUMMARY_PALETTE.ink} opacity='0.95' />
        <polygon points={pointer} fill={SUMMARY_PALETTE.ink} opacity='0.95' />
        <text x={cxp} y={by + 16} textAnchor='middle' fill='#fff' fillOpacity='0.7' fontSize='11'>
          {d.t}
        </text>
        <text x={cxp} y={by + 34} textAnchor='middle' fill='#fff' fontSize='14' fontWeight='700'>
          {valText}
        </text>
      </g>
    )
  }

  return (
    <div ref={wrapRef} style={{ width: '100%', minWidth: 0 }}>
      {W > 0 ? (
        <svg width={W} height={H} style={{ display: 'block', overflow: 'hidden' }}>
          {nodes}
          {tooltip}
        </svg>
      ) : (
        <div style={{ height: H }} />
      )}
    </div>
  )
}

export default MiniChart
